/**
 * IFRAP-AI.fr - Credentials Management Page
 * Admin interface for managing API keys, AI services, email configuration
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/client';
import { Mail, Key, Shield, Send, CheckCircle, XCircle, RefreshCw, Sparkles, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CredentialsPage = () => {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [message, setMessage] = useState(null);
  
  // SMTP State
  const [smtpConfig, setSmtpConfig] = useState({
    SMTP_HOST: '',
    SMTP_PORT: '587',
    SMTP_USER: '',
    SMTP_PASSWORD: '',
    SMTP_FROM: '',
    SMTP_FROM_NAME: 'IFRAP-AI Alertes',
  });
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const fetchCredentials = async () => {
    try {
      const token = localStorage.getItem('ifrap_token');
      const response = await fetch(`${API}/system/credentials`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCredentials(data.credentials);
      }
    } catch (err) {
      console.error('Error fetching credentials:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSmtpStatus = async () => {
    try {
      const res = await api.get('/api/alerts/status');
      setSmtpStatus(res.data.email_service);
    } catch (err) {
      console.error('Error fetching SMTP status:', err);
    }
  };

  useEffect(() => {
    fetchCredentials();
    fetchSmtpStatus();
  }, []);

  const handleSave = async () => {
    const credentialsToSave = Object.entries(editValues)
      .filter(([key, value]) => value && value.trim())
      .map(([key, value]) => ({ key, value }));
    
    if (credentialsToSave.length === 0) {
      setMessage({ type: 'warning', text: 'Aucune valeur à sauvegarder' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('ifrap_token');
      const response = await fetch(`${API}/system/credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credentials: credentialsToSave }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `${data.updated.length} credential(s) sauvegardée(s)` });
        setEditValues({});
        fetchCredentials();
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur réseau' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    try {
      // For now, we just show instructions since we need backend support
      setMessage({ 
        type: 'info', 
        text: 'Configuration SMTP: Ajoutez les variables dans le fichier backend/.env puis redémarrez le service.' 
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'warning', text: 'Entrez une adresse email de test' });
      return;
    }
    
    setTestingEmail(true);
    try {
      const res = await api.post('/api/alerts/test', { email: testEmail });
      if (res.data.success) {
        setMessage({ type: 'success', text: `Email de test envoyé à ${testEmail}` });
      } else {
        setMessage({ type: 'error', text: res.data.error || 'Échec de l\'envoi' });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors du test';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setTestingEmail(false);
    }
  };

  const groupedCredentials = credentials.reduce((acc, cred) => {
    const category = cred.category || 'api';
    if (!acc[category]) acc[category] = [];
    acc[category].push(cred);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#00FF88] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono text-[#00FF88]">
            Configuration Système
          </h1>
          <p className="text-gray-400 mt-1">
            API, messagerie et authentifications
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border flex items-center justify-between ${
          message.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' :
          message.type === 'error' ? 'bg-red-500/10 border-red-500/30 text-red-400' :
          message.type === 'info' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' :
          'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
        }`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-4 opacity-50 hover:opacity-100">×</button>
        </div>
      )}

      {/* AI Services Section */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-purple-400" />
          <div>
            <h2 className="text-lg font-medium text-white">Services IA</h2>
            <p className="text-sm text-gray-500">Clés API pour génération de contenu et présentations</p>
          </div>
        </div>
        <div className="divide-y divide-gray-800">
          {(groupedCredentials.ai || []).map((cred) => (
            <div key={cred.key} className="px-6 py-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-mono text-sm">{cred.key}</span>
                  {cred.is_configured ? (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Configuré
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded flex items-center gap-1">
                      <XCircle className="w-3 h-3" />
                      Non configuré
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-sm mt-1">{cred.description}</p>
                {cred.help_url && (
                  <a
                    href={cred.help_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1 mt-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Obtenir une clé API
                  </a>
                )}
              </div>
              <input
                type="password"
                placeholder={cred.is_configured ? '••••••••' : 'Entrer la clé API'}
                value={editValues[cred.key] || ''}
                onChange={(e) => setEditValues(prev => ({ ...prev, [cred.key]: e.target.value }))}
                className="w-72 px-3 py-2 bg-black/30 border border-gray-700 rounded text-white font-mono text-sm focus:outline-none focus:border-purple-500"
                data-testid={`credential-input-${cred.key}`}
              />
            </div>
          ))}
          {(!groupedCredentials.ai || groupedCredentials.ai.length === 0) && (
            <div className="px-6 py-8 text-center text-gray-500">
              Aucun service IA configuré
            </div>
          )}
        </div>
        {/* Save button for AI credentials */}
        {groupedCredentials.ai && groupedCredentials.ai.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-800 bg-black/20">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Les clés sont stockées dans le fichier backend/.env. Modifiez-les directement ou contactez l'administrateur système.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Email Configuration Section */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center gap-3">
          <Mail className="w-5 h-5 text-[#00FF88]" />
          <div>
            <h2 className="text-lg font-medium text-white">Configuration Email (SMTP)</h2>
            <p className="text-sm text-gray-500">Paramètres Google Workspace / Gmail</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {smtpStatus?.configured ? (
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                Configuré
              </span>
            ) : (
              <span className="flex items-center gap-1 text-yellow-400 text-sm">
                <XCircle className="w-4 h-4" />
                Non configuré
              </span>
            )}
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Instructions */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm">
            <h4 className="text-blue-400 font-medium mb-2">Configuration Google Workspace</h4>
            <ol className="text-gray-300 space-y-1 list-decimal list-inside">
              <li>Connectez-vous à votre compte Google (ifrap.org)</li>
              <li>Allez dans <strong>Paramètres compte Google</strong> → <strong>Sécurité</strong></li>
              <li>Activez la <strong>Validation en 2 étapes</strong> si ce n'est pas fait</li>
              <li>Créez un <strong>Mot de passe d'application</strong> (16 caractères)</li>
              <li>Utilisez ce mot de passe ci-dessous</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Serveur SMTP</label>
              <input
                type="text"
                value={smtpConfig.SMTP_HOST}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, SMTP_HOST: e.target.value })}
                placeholder="smtp.gmail.com"
                className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Port</label>
              <input
                type="text"
                value={smtpConfig.SMTP_PORT}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, SMTP_PORT: e.target.value })}
                placeholder="587"
                className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email expéditeur</label>
              <input
                type="email"
                value={smtpConfig.SMTP_USER}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, SMTP_USER: e.target.value })}
                placeholder="alertes@ifrap.org"
                className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Mot de passe App (16 car.)</label>
              <input
                type="password"
                value={smtpConfig.SMTP_PASSWORD}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, SMTP_PASSWORD: e.target.value })}
                placeholder="xxxx xxxx xxxx xxxx"
                className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
              />
            </div>
          </div>

          {/* Test Email */}
          <div className="pt-4 border-t border-gray-800">
            <label className="block text-sm text-gray-400 mb-2">Tester l'envoi d'email</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="votre@email.com"
                className="flex-1 px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
              />
              <button
                onClick={handleTestEmail}
                disabled={testingEmail || !smtpStatus?.configured}
                className="flex items-center gap-2 px-4 py-2 bg-[#00FF88] text-black rounded font-medium disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {testingEmail ? 'Envoi...' : 'Tester'}
              </button>
            </div>
            {!smtpStatus?.configured && (
              <p className="text-yellow-400 text-xs mt-2">
                Configurez d'abord les paramètres SMTP dans le fichier backend/.env
              </p>
            )}
          </div>

          {/* ENV Variables to add */}
          <div className="bg-black/30 rounded p-4 font-mono text-xs">
            <p className="text-gray-500 mb-2"># À ajouter dans backend/.env</p>
            <pre className="text-gray-300">
{`SMTP_HOST=${smtpConfig.SMTP_HOST || 'smtp.gmail.com'}
SMTP_PORT=${smtpConfig.SMTP_PORT || '587'}
SMTP_USER=${smtpConfig.SMTP_USER || 'alertes@ifrap.org'}
SMTP_PASSWORD=${smtpConfig.SMTP_PASSWORD || 'votre-mot-de-passe-app'}
SMTP_FROM=${smtpConfig.SMTP_USER || 'alertes@ifrap.org'}
SMTP_FROM_NAME=IFRAP-AI Alertes`}
            </pre>
          </div>
        </div>
      </div>

      {/* API Credentials */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center gap-3">
          <Key className="w-5 h-5 text-purple-400" />
          <div>
            <h2 className="text-lg font-medium text-white">Sources de Données API</h2>
            <p className="text-sm text-gray-500">Clés d'accès aux API économiques</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || Object.keys(editValues).length === 0}
            className="ml-auto px-4 py-2 bg-[#00FF88] text-black font-medium rounded hover:bg-[#00FF88]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
        <div className="divide-y divide-gray-800">
          {(groupedCredentials.api || []).map((cred) => (
            <div key={cred.key} className="px-6 py-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-mono text-sm">{cred.key}</span>
                  {cred.is_configured && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                      Configuré
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-sm mt-1">{cred.source}</p>
              </div>
              <input
                type="password"
                placeholder={cred.is_configured ? '••••••••' : 'Entrer la valeur'}
                value={editValues[cred.key] || ''}
                onChange={(e) => setEditValues(prev => ({ ...prev, [cred.key]: e.target.value }))}
                className="w-64 px-3 py-2 bg-black/30 border border-gray-700 rounded text-white font-mono text-sm focus:outline-none focus:border-[#00FF88]"
              />
            </div>
          ))}
          {(!groupedCredentials.api || groupedCredentials.api.length === 0) && (
            <div className="px-6 py-8 text-center text-gray-500">
              Aucune clé API requise - Open Data uniquement
            </div>
          )}
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-[#00FF88] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-white font-medium">Sécurité</h3>
            <p className="text-gray-500 text-sm mt-1">
              Les credentials sont stockées de manière sécurisée côté serveur.
              Les mots de passe ne sont jamais renvoyés au client après sauvegarde.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialsPage;
